// Shared building blocks for the booking analytics dashboards (farmer + admin).
// Registers Chart.js once and exposes reusable cards + colour constants in the
// existing ChickFarm green theme.
import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const STATUS_ORDER = ['pending', 'accepted', 'completed', 'declined', 'canceled'];

export const STATUS_COLORS = {
  pending: '#d97706', // amber
  accepted: '#2563eb', // blue
  completed: '#1e5c33', // primary green
  declined: '#dc2626', // red
  canceled: '#6b7280', // gray
};

export const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  completed: 'Completed',
  declined: 'Declined',
  canceled: 'Canceled',
};

// Summary stat card.
export function StatCard({ label, value, icon, accent = 'text-primary', sub }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
        {icon && <span className={accent}>{icon}</span>}
      </div>
      <div className="text-2xl font-bold text-gray-800 dark:text-white mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

// Chart container with a title.
export function ChartCard({ title, children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm ${className}`}>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">{title}</h3>
      {children}
    </div>
  );
}

// Small coloured status pill (e.g. for tables).
export function StatusPill({ status, count }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium"
      style={{ color: STATUS_COLORS[status] }}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[status] }} />
      {count}
    </span>
  );
}

export function statusDoughnutData(status) {
  const keys = STATUS_ORDER.filter((k) => (status?.[k] || 0) > 0);
  const labels = keys.length ? keys : STATUS_ORDER;
  return {
    labels: labels.map((k) => STATUS_LABELS[k]),
    datasets: [
      {
        data: labels.map((k) => status?.[k] || 0),
        backgroundColor: labels.map((k) => STATUS_COLORS[k]),
        borderWidth: 0,
      },
    ],
  };
}

export const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
};

export const baseBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: '#f1f5f9' } },
  },
};

export const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: '#f1f5f9' } },
  },
};

// ─── Paste these additions into analyticsShared.jsx ──────────────────────────

// ── Waste-specific status colours ────────────────────────────────────────────
export const WASTE_STATUS_COLORS = {
  pending:   '#f59e0b',
  bidding:   '#3b82f6',
  accepted:  '#8b5cf6',
  completed: '#1e5c33',
  canceled:  '#ef4444',
};

// ── Waste doughnut helper ────────────────────────────────────────────────────
export function wasteStatusDoughnutData(status) {
  return {
    labels: ['Pending', 'Bidding', 'Accepted', 'Completed', 'Canceled'],
    datasets: [
      {
        data: [
          status.pending   || 0,
          status.bidding   || 0,
          status.accepted  || 0,
          status.completed || 0,
          status.canceled  || 0,
        ],
        backgroundColor: Object.values(WASTE_STATUS_COLORS),
        borderWidth: 0,
      },
    ],
  };
}

// ── Bid-status colours ────────────────────────────────────────────────────────
export const BID_STATUS_COLORS = {
  pending:   '#f59e0b',
  accepted:  '#1e5c33',
  declined:  '#ef4444',
  rejected:  '#6b7280',
  completed: '#3b82f6',
};

// ── Pagination component ──────────────────────────────────────────────────────
// Usage:
//   const [page, setPage] = useState(1);
//   const PAGE_SIZE = 10;
//   const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
//   <Pagination page={page} setPage={setPage} total={items.length} pageSize={PAGE_SIZE} />

// ── Plan badge (subscription tier pill) ───────────────────────────────────────
export const PLAN_LABELS = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  '2years': '2-Year',
  '3years': '3-Year',
};

export function PlanBadge({ plan, active }) {
  if (!plan) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200">
        No plan
      </span>
    );
  }
  const cls = active
    ? 'bg-green-50 text-green-700 ring-green-200'
    : 'bg-gray-50 text-gray-500 ring-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${cls}`}>
      {PLAN_LABELS[plan] || plan}
    </span>
  );
}

// ── Empty state block for any analytics page ──────────────────────────────────
export function EmptyState({ icon, title, message }) {
  return (
    <div className="max-w-xl mx-auto mt-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
      {icon && <div className="mx-auto text-3xl text-primary mb-3">{icon}</div>}
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h2>
      {message && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{message}</p>}
    </div>
  );
}

// ── Company scope banner ──────────────────────────────────────────────────────
// Shown at the top of company-type dashboards so it's clear the figures are
// scoped to that organization's own account, not platform-wide.
export function CompanyScopeBanner({ companyName }) {
  return (
    <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
      <FaBuildingIcon />
      <span>
        Showing data for <strong>{companyName || 'your organization'}</strong> only.
      </span>
    </div>
  );
}

function FaBuildingIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 21V3h10v6h6v12h-6v-4h-2v4H4Zm2-2h2v-2H6v2Zm0-4h2v-2H6v2Zm0-4h2V9H6v2Zm0-4h2V5H6v2Zm4 8h2v-2h-2v2Zm0-4h2V9h-2v2Zm0-4h2V5h-2v2Zm6 12h2v-2h-2v2Zm0-4h2v-2h-2v2Z" />
    </svg>
  );
}

export function Pagination({ page, setPage, total, pageSize = 10 }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
      <span>
        {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
        >
          ‹
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-2 py-1">…</span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-2 py-1 rounded border ${
                p === page
                  ? 'bg-primary text-white border-primary'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
        >
          ›
        </button>
      </div>
    </div>
  );
}
